import Head from "next/head"
import { useRouter } from "next/router"
import { useTranslations } from "next-intl"
import HrefLang from "@/components/HrefLang"
import useMemberStore from "@/store/MemberStore"
import * as amplitude from "@amplitude/analytics-browser"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { GetServerSidePropsContext } from "next"

function ConnectThreadsAccountPage() {
  const t = useTranslations("ConnectThreadsAccountPage")
  const isLoggedIn = useMemberStore((state) => state.checkIsLoggedIn())
  const title = t("Meta.title")
  const description = t("Meta.description")
  const router = useRouter()
  const { locale } = router
  const localeToRedirectUri = {
    "zh-TW": process.env.NEXT_PUBLIC_THREADS_REDIRECT_URI_TW,
    "ja-JP": process.env.NEXT_PUBLIC_THREADS_REDIRECT_URI_JA,
  }
  const redirectUri =
    localeToRedirectUri[locale] ||
    process.env.NEXT_PUBLIC_THREADS_REDIRECT_URI_EN

  const url = `${process.env.NEXT_PUBLIC_THREADS_CALLBACK_URI}&redirect_uri=${redirectUri}`
  const handleButtonClicked = () => {
    if (!isLoggedIn) {
      router.push("/signup")
      return
    }
    router.push(url)
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <HrefLang />
      <Head>
        <title>{title}</title>
        <meta name="og:title" content={title} />
        <meta name="og:description" content={description} />
        <meta name="description" content={description} />
      </Head>
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto">
          {/* Main heading */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {t("Title.connect")}
            </h1>
            {/* Divider */}
            <div className="w-24 h-1 bg-red-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 leading-relaxed">
              {t("Message.connect")}
            </p>
          </div>

          {/* Connect Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
            <Button
              size="lg"
              className="w-full bg-red-500 hover:bg-red-600 text-lg py-6"
              onClick={handleButtonClicked}>
              {t("Action.connect")}
            </Button>

            <p className="mt-4 text-sm text-gray-500 text-center">
              {t("Message.privacyNotice")}
            </p>

            {/* FAQ Section */}
            <div className="mt-8">
              <Separator className="mb-6" />
              <Accordion type="multiple" defaultValue={["item-1"]}>
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-left">
                    <span className="text-base font-medium text-gray-900">
                      {t("Title.question1")}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-gray-600">
                      {t("Message.threadsPasswordDisclaimer2")}
                    </p>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-left">
                    <span className="text-base font-medium text-gray-900">
                      {t("Title.question2")}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-gray-600">{t("Message.question2")}</p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  return {
    props: {
      messages: (await import(`@/messages/${context.locale}.json`)).default,
    },
  }
}

export default ConnectThreadsAccountPage
